import React, { useMemo, useRef } from 'react';
import { MultiSeriesDataPoint } from '@moderepo/mode-app-components';
import { Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { NumericMetricInfo } from '@moderepo/mode-internal-apis';


ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);



export interface SparklineChartProps {
    readonly className?: string | undefined;
    readonly dataPoints: readonly MultiSeriesDataPoint<number>[];
    readonly metrics: NumericMetricInfo | readonly NumericMetricInfo[];
    readonly color?: string | undefined;
}



export const SparklineChart: React.FC<SparklineChartProps> = (props: SparklineChartProps) => {
    const options = useMemo(() => {
        return {
            responsive: true,
            normalized: true,
            scales    : {
                x: {
                    display: false,
                },
                y: {
                    display: false,
                },
            },
            elements: {
                line: {
                    borderColor: props.color,
                    borderWidth: 2,
                },
                point: {
                    radius: 0,
                },
            },
            tooltips: {
                enabled: false,
            },
            events : [],
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: false,
                },
            },
        };
    }, [props.color]);

    const chartRef = useRef<ChartJS<'line', MultiSeriesDataPoint<number>[]> | undefined>(null);

    /**
     * Convert props.dataPoints and props.metric into the data structure that ChartJS can work with
     */
    const data = useMemo(() => {
        if (chartRef.current) {
            // Clear previous data
            const chart = chartRef.current;
            chart.data.labels = [];
            chart.data.datasets = [];
            chart.update();
        }

        const datasets = (props.metrics instanceof Array ? props.metrics : [props.metrics]).map((metric) => {
            return {
                data: props.dataPoints.map((dataPoint) => {
                    if (!dataPoint.dateString) {
                        return {
                            ...dataPoint,
                            dateString: dataPoint.date.toISOString(),
                        };
                    }
                    return dataPoint;
                }),
                parsing: {
                    xAxisKey: 'dateString',
                    yAxisKey: `values.${metric.name}`,
                },
            };
        });

        return {
            datasets,
        };
    }, [props.dataPoints, props.metrics]);



    return (
        <div className={props.className}>
            <Line
                ref={chartRef}
                options={options}
                data={data}
                style={{ width: '100%', height: '100%' }}
            />
        </div>
    );
};
